/**
 * player_city_lights — the player cities as the night sees them (agent P, 2026-10-06: "player cities get no city lights
 * yet"). Which `city` rows give light, what stands in each, and a cheap change stamp. Read by AtlasSkyglowModule (the
 * light-pollution sources: the sky glow, the domes, the clouds' uplight) and CityNightLightsModule (the carpet and the
 * lamp heads) from the rows the client already holds: `city` and `structure` ride the base subscription, and
 * `city_allegiance` is asked for here (one small public table). No three, no DOM; the harnesses feed it fake tables.
 *
 * WHO BELONGS: a structure in the Commons world whose `structure.cityId` names the city (the `structureByCity` index),
 * plus an UNCLAIMED one (cityId 0) standing inside the city's radius — a deed placed since the last city pulse; the pulse
 * (`recomputeMembership`, server/src/reducers/cities.ts) claims exactly those, so the night never waits up to five
 * minutes for a new house to light. City Halls stand ≥ 1 km apart and a radius is ≤ 450 m, so no unclaimed deed can
 * sit in two cities.
 *
 * NO PER-FRAME WALKS: `playerCitiesStamp` is three table stamps (lib/fast_find `tableStamp`: the mutation counter and the
 * row count); the callers gather only when it moves. An unpatched table stamps NaN (the kill switch): the callers then
 * gather on a slow clock instead — the old cost, never a stale answer.
 */
import { tableStamp } from './fast_find.js';
import { glowCivOfAlignment, playerCityLive, playerCityGlowSite, type GlowSite } from './light_pollution.js';
import { isLitPlayerStructure, type PlayerStructureLite } from './city_night_lights.js';

export interface PlayerCityLive {
  /** `p<cityId>` — the light source's id (light_pollution's reserved form). */
  key: string;
  cityId: number;
  name: string;
  centerX: number; centerZ: number;
  rank: number; radiusM: number; status: string;
  /** The allegiance as `city_allegiance.civ` spells it ('a' | 'b' | '' | 'p:<civId>'), and as a glow civ. */
  alignment: string;
  civ: 'a' | 'b' | '';
  /** Every structure of the city that gives light, sorted by id. */
  structures: PlayerStructureLite[];
  /** How many (light_pollution's build count). */
  lit: number;
  /** Everything a drawing depends on — a changed signature regenerates the city's points. */
  sig: string;
}

interface CityRowLike { cityId: bigint | number; name?: string; centerX: number; centerZ: number; cityRank: number; radiusM: number; status: string }
interface StructureRowLike { structureId: bigint | number; worldId: bigint | number; structureType: string; posX: number; posZ: number; yaw: number; cityId: bigint | number }
export interface PlayerCityDb {
  city?: { iter(): Iterable<CityRowLike> } | null;
  structure?: { iter(): Iterable<StructureRowLike>; structureByCity?: { filter(id: bigint): Iterable<StructureRowLike> } } | null;
  cityAllegiance?: { cityId?: { find(id: bigint): { civ: string } | null | undefined } } | null;
}

/** The change stamp of the three tables a player city's light reads ('NaN' inside = an unpatched table: gather on a clock). */
export function playerCitiesStamp(db: PlayerCityDb | null | undefined): string {
  if (!db) return 'none';
  return `${tableStamp(db.city)}|${tableStamp(db.structure)}|${tableStamp(db.cityAllegiance)}`;
}

const big = (v: bigint | number): bigint => (typeof v === 'bigint' ? v : BigInt(Math.trunc(Number(v) || 0)));

/** Every LIVE player city (active / provisional) in the Commons with the structures that light it. */
export function gatherPlayerCities(db: PlayerCityDb | null | undefined, commonsWorldId: bigint | number | null | undefined): PlayerCityLive[] {
  const out: PlayerCityLive[] = [];
  if (!db?.city || !db.structure) return out;
  const world = commonsWorldId == null ? null : big(commonsWorldId);
  const inWorld = (s: StructureRowLike): boolean => world === null || big(s.worldId) === world;
  const byCity = db.structure.structureByCity;
  // Without the index (a fake table, an older binding) the structures are bucketed by one walk.
  let buckets: Map<bigint, StructureRowLike[]> | null = null;
  const of = (id: bigint): StructureRowLike[] => {
    if (byCity) return [...byCity.filter(id)];
    if (!buckets) { buckets = new Map(); for (const s of db.structure!.iter()) { const k = big(s.cityId); const l = buckets.get(k); if (l) l.push(s); else buckets.set(k, [s]); } }
    return buckets.get(id) ?? [];
  };
  let unclaimed: StructureRowLike[] | null = null;
  for (const c of db.city.iter()) {
    if (!playerCityLive(c.status) || !Number.isFinite(c.centerX) || !Number.isFinite(c.centerZ)) continue;
    const cid = big(c.cityId);
    const seen = new Set<string>();
    const structures: PlayerStructureLite[] = [];
    const take = (s: StructureRowLike): void => {
      const id = String(s.structureId);
      if (seen.has(id) || !inWorld(s) || !isLitPlayerStructure(s.structureType) || !Number.isFinite(s.posX) || !Number.isFinite(s.posZ)) return;
      seen.add(id);
      structures.push({ id: Number(s.structureId), type: s.structureType, x: s.posX, z: s.posZ, yaw: Number.isFinite(s.yaw) ? s.yaw : 0 });
    };
    for (const s of of(cid)) take(s);
    unclaimed ??= of(0n);
    const r = Number(c.radiusM) > 0 ? Number(c.radiusM) : 150;
    for (const s of unclaimed) if (Math.hypot(s.posX - c.centerX, s.posZ - c.centerZ) <= r) take(s);
    structures.sort((a, b) => a.id - b.id);
    const alignment = db.cityAllegiance?.cityId?.find(cid)?.civ ?? '';
    const civ = glowCivOfAlignment(alignment);
    const cityId = Number(cid);
    const rank = Math.round(Number(c.cityRank)) || 1;
    out.push({
      key: `p${cityId}`, cityId, name: c.name ?? '', centerX: c.centerX, centerZ: c.centerZ, rank, radiusM: r, status: c.status,
      alignment, civ, structures, lit: structures.length,
      sig: `${rank}|${r}|${civ}|${c.status}|${c.centerX.toFixed(1)},${c.centerZ.toFixed(1)}|` +
        structures.map((s) => `${s.id}:${s.type}:${s.x.toFixed(2)},${s.z.toFixed(2)},${s.yaw.toFixed(3)}`).join(';'),
    });
  }
  return out;
}

/** The live cities as contract sites (`p<cityId>`, tier `player`, sized by rank and build — light_pollution). */
export function playerGlowSites(cities: ReadonlyArray<PlayerCityLive>): GlowSite[] {
  const out: GlowSite[] = [];
  for (const c of cities) {
    const s = playerCityGlowSite({ cityId: c.cityId, centerX: c.centerX, centerZ: c.centerZ, cityRank: c.rank, radiusM: c.radiusM, status: c.status }, c.alignment, c.lit);
    if (s) out.push(s);
  }
  return out;
}

/**
 * `city_allegiance` (a city's civ — its glow tint) is not in the base subscription; ask once per connection. An older
 * publish without the table fails alone and the cities glow neutral. Rows the client already holds cost nothing more.
 */
let subsFor: unknown = null;
export function ensurePlayerCitySubs(conn: unknown): void {
  const c = conn as { subscriptionBuilder?: () => { onError(cb: () => void): { subscribe(sql: string[]): void } } } | null;
  if (!c || typeof c.subscriptionBuilder !== 'function' || subsFor === conn) return;
  subsFor = conn;
  try { c.subscriptionBuilder().onError(() => { /* an older publish: neutral glow */ }).subscribe(['SELECT * FROM city_allegiance']); } catch { /* a bed, a harness */ }
}
